import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { App, Button, DatePicker, Input, Modal, Radio, Select, Table, Tabs, Upload } from 'antd';
import { DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import DOLayout from '../../components/directOrders/DOLayout';
import { Reference, STATUS_OPTIONS, StatusBadge } from '../../components/cfq/CfqParts';
import { useAuth } from '../../context/AuthContext';
import cfqService from '../../services/cfqService';
import { openApiFile } from '../../utils/apiAuth';
import { formatDate } from '../../utils/workingDays';

const byNewest = (a, b) => b.year - a.year || b.number - a.number;
const dateColumn = (title, key) => ({ title, dataIndex: key, key, width: 120, render: (iso) => <span className="do-num">{formatDate(iso) || '—'}</span> });

const referenceColumn = { title: 'Reference', key: 'reference', width: 140, render: (_, r) => <Reference request={r} /> };
const statusColumn = { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: (s) => <StatusBadge status={s} /> };

/** The person's own CfQs, newest first. */
const MyCfqs = ({ onOpen }) => {
  const { message } = App.useApp();
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    cfqService.mine().then(setRequests).catch((err) => {
      message.error(err.message);
      setRequests([]);
    });
  }, [message]);

  if (requests && requests.length === 0) {
    return (
      <div className="do-empty">
        <strong>You haven’t requested a CfQ reference yet</strong>
        Request one before you send a call for quotes to suppliers.
      </div>
    );
  }

  return (
    <Table
      className="cfq-table"
      rowKey="id"
      loading={!requests}
      dataSource={requests || []}
      pagination={{ pageSize: 25, hideOnSinglePage: true }}
      scroll={{ x: 820 }}
      onRow={(r) => ({ onClick: () => onOpen(r) })}
      columns={[
        referenceColumn,
        {
          title: 'Description',
          key: 'description',
          render: (_, r) => (
            <>
              {r.description}
              {r.festival && <span className="cfq-table__festival">{r.festival}</span>}
            </>
          ),
        },
        dateColumn('Issue', 'issue_date'),
        dateColumn('Closing', 'closing_date'),
        statusColumn,
      ]}
    />
  );
};

/** A whole year, or the requests made between two dates, in the Excel register's columns. */
const ExportDialog = ({ open, years, defaultYear, onClose }) => {
  const { message } = App.useApp();
  const [mode, setMode] = useState('year');
  const [year, setYear] = useState(defaultYear);
  const [range, setRange] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => setYear(defaultYear), [defaultYear]);

  const period =
    mode === 'year' ? (year ? { year } : null) : range ? { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') } : null;

  const run = async () => {
    setBusy(true);
    try {
      const name = period.year ? `cfq_register_${period.year}` : `cfq_register_${period.from}_to_${period.to}`;
      await openApiFile(cfqService.exportUrl(period), { download: true, filename: `${name}.csv` });
      onClose();
    } catch (err) {
      message.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Export to Excel" open={open} onCancel={onClose} onOk={run} okText="Export" okButtonProps={{ disabled: !period, loading: busy }}>
      <p className="do-muted">The same columns as the Excel register, oldest first.</p>
      <Radio.Group
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        options={[
          { value: 'year', label: 'A whole year' },
          { value: 'range', label: 'A date range' },
        ]}
        style={{ marginBottom: 16 }}
      />
      {mode === 'year' ? (
        <Select aria-label="Year" style={{ width: 140 }} value={year} options={years.map((y) => ({ value: y, label: y }))} onChange={setYear} />
      ) : (
        <>
          <DatePicker.RangePicker format="DD.MM.YYYY" value={range} onChange={setRange} aria-label="Requested between" />
          <p className="do-panel__hint" style={{ marginTop: 8 }}>CfQs requested on these days, both included.</p>
        </>
      )}
    </Modal>
  );
};

/** Every CfQ of a year, for Admins and CFQ Admins: filters, export and the SOP upload. */
const Register = ({ onOpen }) => {
  const { message } = App.useApp();
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [festival, setFestival] = useState(null);
  const [status, setStatus] = useState(null);
  const [sop, setSop] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setData(null);
    cfqService.register(year).then(setData).catch((err) => message.error(err.message));
  }, [year, message]);

  useEffect(() => {
    cfqService.sop().then(setSop).catch(() => setSop(null));
  }, []);

  const requests = useMemo(() => data?.requests || [], [data]);
  const festivals = useMemo(() => [...new Set(requests.map((r) => r.festival).filter(Boolean))].sort(), [requests]);
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return requests
      .filter((r) => !festival || r.festival === festival)
      .filter((r) => !status || r.status === status)
      .filter((r) => !term || [`cfq${r.year}/${r.number}`, r.description, r.festival, r.requester_name].some((v) => v && v.toLowerCase().includes(term)))
      .sort(byNewest);
  }, [requests, search, festival, status]);

  const thisYear = data && data.year === data.years[0];
  const next = requests.reduce((max, r) => Math.max(max, r.number), 0) + 1;

  const uploadSop = async (file) => {
    setUploading(true);
    try {
      setSop(await cfqService.uploadSop(file));
      message.success('New SOP uploaded. Everyone now downloads this version.');
    } catch (err) {
      message.error(err.message);
    } finally {
      setUploading(false);
    }
    return Upload.LIST_IGNORE;
  };

  return (
    <>
      <div className="cfq-toolbar">
        <Input.Search
          className="cfq-toolbar__search"
          placeholder="Reference, description or officer"
          allowClear
          aria-label="Search the register"
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          aria-label="Year"
          style={{ width: 110 }}
          value={data?.year}
          options={(data?.years || []).map((y) => ({ value: y, label: y }))}
          onChange={setYear}
        />
        <Select
          aria-label="Festival or event"
          style={{ width: 220 }}
          allowClear
          showSearch
          placeholder="All festivals and events"
          value={festival}
          options={festivals.map((f) => ({ value: f, label: f }))}
          onChange={(v) => setFestival(v ?? null)}
        />
        <Select
          aria-label="Status"
          style={{ width: 150 }}
          allowClear
          placeholder="All statuses"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(v) => setStatus(v ?? null)}
        />
        <Button icon={<DownloadOutlined />} onClick={() => setExporting(true)} disabled={!data}>
          Export to Excel
        </Button>
        <Upload accept=".pdf,.doc,.docx" showUploadList={false} beforeUpload={uploadSop}>
          <Button icon={<UploadOutlined />} loading={uploading}>
            Upload new SOP
          </Button>
        </Upload>
        {thisYear && (
          <div className="cfq-next" aria-label={`Next reference CFQ${data.year}/${next}`}>
            <span className="cfq-next__label">Next reference</span>
            <span className="cfq-next__ref">
              <span>CFQ{data.year}/</span>
              {next}
            </span>
          </div>
        )}
      </div>
      <div className="cfq-toolbar__meta">
        <span>
          Showing {shown.length} of {requests.length} references{data ? ` in ${data.year}` : ''}
        </span>
        <span>
          {sop
            ? `Current SOP: ${sop.file_name}, uploaded ${new Date(sop.uploaded_at).toLocaleDateString('en-GB')}`
            : 'No SOP uploaded yet. Upload one so staff can download it.'}
        </span>
      </div>
      <Table
        className="cfq-table"
        rowKey="id"
        loading={!data}
        dataSource={shown}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        scroll={{ x: 1040 }}
        onRow={(r) => ({ onClick: () => onOpen(r) })}
        locale={{ emptyText: requests.length ? 'No references match these filters.' : 'No references in this year yet.' }}
        columns={[
          referenceColumn,
          { title: 'Description', dataIndex: 'description', key: 'description' },
          { title: 'Festival or event', dataIndex: 'festival', key: 'festival', width: 200, render: (f) => f || '—' },
          { title: 'Requested by', dataIndex: 'requester_name', key: 'requester_name', width: 160 },
          { title: 'Method', dataIndex: 'method', key: 'method', width: 90, render: (m) => m || '—' },
          dateColumn('Issue', 'issue_date'),
          dateColumn('Closing', 'closing_date'),
          statusColumn,
        ]}
      />
      <ExportDialog open={exporting} years={data?.years || []} defaultYear={data?.year} onClose={() => setExporting(false)} />
    </>
  );
};

const CallForQuote = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canSeeRegister = Boolean(user?.isCfqAdmin);
  const tab = canSeeRegister && params.get('tab') === 'register' ? 'register' : 'mine';
  const open = useCallback((r) => navigate(`/cfq/${r.id}`), [navigate]);

  const request = (
    <Link to="/cfq/new">
      <Button type="primary" icon={<PlusOutlined />}>
        Request a reference
      </Button>
    </Link>
  );

  return (
    <DOLayout
      title="Call for Quote"
      subtitle="Every call sent to suppliers needs a reference. Numbers are given out in order and start again from 1 each January."
      actions={request}
      showNav={false}
    >
      {canSeeRegister ? (
        <Tabs
          activeKey={tab}
          onChange={(key) => setParams(key === 'register' ? { tab: 'register' } : {}, { replace: true })}
          items={[
            { key: 'mine', label: 'My CfQs', children: <MyCfqs onOpen={open} /> },
            { key: 'register', label: 'Register', children: <Register onOpen={open} /> },
          ]}
        />
      ) : (
        <div style={{ marginTop: 24 }}>
          <MyCfqs onOpen={open} />
        </div>
      )}
    </DOLayout>
  );
};

export default CallForQuote;
